import React, { useEffect, useRef, useState } from 'react';
import { Mail, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { API_URL } from '../../config';
import { apiClient } from '../../utils/apiClient';

/**
 * Double-Opt-In: Landing-Seite für den Verifikations-Link aus der E-Mail.
 * Liest `?token=...`, ruft /api/auth/verify-email, speichert JWT, loggt den
 * DSGVO-Consent und übergibt den User an App (handleAuthSuccess) → Dashboard
 * → Willkommens-Tour startet automatisch (onboarding_completed=false).
 */
export default function VerifyEmailPage({ onSuccess }) {
  const [state, setState] = useState('loading'); // 'loading' | 'success' | 'error' | 'expired'
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) {
      setState('error');
      setMessage('Es wurde kein Bestätigungs-Token in der URL gefunden.');
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });
        const data = await res.json();
        if (!res.ok) {
          const detail = data?.detail || 'Ein Fehler ist aufgetreten.';
          setMessage(detail);
          setState(/abgelaufen/i.test(detail) ? 'expired' : 'error');
          return;
        }

        // JWT + User speichern
        localStorage.setItem('snapshot_token', data.token);
        localStorage.setItem('snapshot_user', JSON.stringify(data.user));
        localStorage.setItem('snapshot_beta_access', 'granted');
        localStorage.removeItem('generations_cache');
        localStorage.removeItem('last_known_avatars');
        localStorage.removeItem('gallery_cache');

        // DSGVO-Consent protokollieren (nachgezogen aus dem Register-Schritt).
        try {
          const ipHash = await crypto.subtle
            .digest('SHA-256', new TextEncoder().encode('client-ip-placeholder'))
            .then((buf) => Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join(''));
          await apiClient.post('/api/consents', {
            user_id: data.user.id,
            email: data.user.email,
            avatar_id: null,
            policy_version: '2025-11-01',
            consent_type: 'registration',
            consents: { terms: true, privacy: true, imageProcessing: true, age: true },
            ip_hash: ipHash,
            timestamp: new Date().toISOString()
          });
        } catch (err) {
          console.warn('Consent logging nach Verify fehlgeschlagen (non-critical):', err);
        }

        setState('success');

        // Kurze Pause, damit der User die Erfolgsmeldung sieht, dann weiter.
        setTimeout(() => {
          if (onSuccess) {
            onSuccess(data.user, true);
          } else {
            window.location.href = '/dashboard';
          }
        }, 1200);
      } catch (err) {
        console.error('verify-email error:', err);
        setState('error');
        setMessage('Netzwerkfehler. Bitte prüfe deine Verbindung und lade die Seite neu.');
      }
    })();
  }, [onSuccess]);

  const handleResend = async () => {
    if (!resendEmail.trim() || isResending) return;
    setIsResending(true);
    setResendMessage('');
    try {
      await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resendEmail.trim().toLowerCase() })
      });
      setResendMessage('Falls dieser Account existiert, haben wir den Link erneut verschickt.');
    } catch (err) {
      setResendMessage('Senden fehlgeschlagen. Bitte später erneut versuchen.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-black grain-texture flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md text-center">
        {state === 'loading' && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
              <Loader className="w-8 h-8 text-white animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Bestätige deine E-Mail-Adresse …</h1>
            <p className="text-white/60">Einen Moment bitte.</p>
          </>
        )}

        {state === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#10B981]/20 border border-[#10B981]/40 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-[#10B981]" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">E-Mail bestätigt</h1>
            <p className="text-white/70">Du wirst gleich automatisch eingeloggt und die Tour startet.</p>
          </>
        )}

        {(state === 'error' || state === 'expired') && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {state === 'expired' ? 'Link abgelaufen' : 'Bestätigung fehlgeschlagen'}
            </h1>
            <p className="text-white/70 mb-6">{message}</p>

            <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-left">
              <p className="text-white/80 text-sm mb-2">Neuen Link anfordern:</p>
              <input
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="deine@mail.de"
                className="w-full px-3 py-2 rounded-lg bg-black border border-white/20 text-white placeholder-white/30 mb-2"
              />
              <button
                type="button"
                onClick={handleResend}
                disabled={isResending || !resendEmail.trim()}
                className="w-full py-2 rounded-lg bg-[#10B981] text-black font-semibold disabled:opacity-50"
              >
                {isResending ? 'Wird gesendet …' : 'Neuen Link senden'}
              </button>
              {resendMessage && <p className="text-center text-sm text-white/60 mt-2">{resendMessage}</p>}
            </div>

            <a href="/login" className="block mt-6 text-white/50 hover:text-white/80 text-sm underline">
              Zurück zur Anmeldung
            </a>
          </>
        )}
      </div>
    </div>
  );
}
